import React, { useState } from 'react';
import Modal from '../../components/Modal';
import PhoneInput from '../../components/PhoneInput';
import './style.css';
import { useLanguage } from '../../contexts/LanguageContext';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const ForgotPasswordModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { language } = useLanguage();
  const [resetType, setResetType] = useState<'email' | 'phone'>('phone');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('+380');
  const [phone, setPhone] = useState('');
  const [fullPhone, setFullPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handlePhoneInputChange = (fullPhone: string, code: string, localNumber: string) => {
    setFullPhone(fullPhone);
    setCountryCode(code);
    setPhone(localNumber);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(null);

    try {
      const requestData = resetType === 'email'
        ? { email }
        : { phone: fullPhone };

      const response = await fetch('/api/v1/auth/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage(data.message || (language === 'ua' ? 'Інструкції для скидання пароля відправлено' : 'Password reset instructions sent'));
        setIsSubmitted(true);
      } else {
        setMessage(data.error || (language === 'ua' ? 'Помилка при відправці' : 'Error sending reset instructions'));
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setMessage(language === 'ua' ? 'Помилка мережі' : 'Network error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnBackdrop={false}>
      <div className="forgot-header">
        <i className="modal-icon bi bi-key-fill"></i>
        <h2>{language === 'ua' ? 'Відновлення пароля' : 'Forgot Password'}</h2>
        <p className="forgot-sub">{language === 'ua' ? 'Введіть email або номер телефону, щоб отримати інструкції для скидання' : 'Enter your email or phone number to receive reset instructions'}</p>
      </div>

      <form className="forgot-form" onSubmit={handleSubmit}>
        {/* Reset type selector */}
        <div className="reset-type-selector">
          <button
            type="button"
            className={`type-button ${resetType === 'phone' ? 'active' : ''}`}
            onClick={() => {
              setResetType('phone');
              setEmail('');
              setPhone('');
              setMessage(null);
              setIsSubmitted(false);
            }}
            title={language === 'ua' ? 'Відновлення за телефоном' : 'Phone reset'}
            disabled={isSubmitted}
          >
            <i className="bi bi-phone"></i>
            <span className="button-text">{language === 'ua' ? 'Тел' : 'Tel'}</span>
          </button>
          <button
            type="button"
            className={`type-button ${resetType === 'email' ? 'active' : ''}`}
            onClick={() => {
              setResetType('email');
              setEmail('');
              setPhone('');
              setMessage(null);
              setIsSubmitted(false);
            }}
            title={language === 'ua' ? 'Відновлення за email' : 'Email reset'}
            disabled={isSubmitted}
          >
            <i className="bi bi-envelope"></i>
            <span className="button-text">Email</span>
          </button>
        </div>

        {/* Email or phone input - hidden after submission */}
        {!isSubmitted && (resetType === 'email' ? (
          <div className="form-group">
            <label htmlFor="fp-email">
              <i className="bi bi-envelope"></i>
              {language === 'ua' ? 'Email адреса' : 'Email Address'}
            </label>
            <input
              id="fp-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@email.com"
              autoComplete="email"
            />
          </div>
        ) : (
          <div className="form-group">
            <label htmlFor="fp-phone">
              <i className="bi bi-phone"></i>
              {language === 'ua' ? 'Номер телефону' : 'Phone Number'}
            </label>
            <PhoneInput
              id="fp-phone"
              countryCode={countryCode}
              localNumber={phone}
              onChange={handlePhoneInputChange}
              placeholder={language === 'ua' ? 'XX XXX XX XX' : 'XX XXX XX XX'}
              required
            />
          </div>
        ))}

        {message && <div className="fp-message">{message}</div>}

        {!isSubmitted && (
          <button className="fp-submit" type="submit" disabled={isLoading}>
            {isLoading ? (language === 'ua' ? 'Відправка...' : 'Sending...') : (language === 'ua' ? 'Відправити' : 'Send')}
          </button>
        )}
      </form>
    </Modal>
  );
};

export default ForgotPasswordModal;
